import { useAuth0 } from '@auth0/auth0-react';
import { formatAddress, formatGender, formatNameLastFirst } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { FormHeaderAndContent } from '@strata/react-form-header-and-content';
import { FormAccordion } from '@strata/react-form-section';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { FormTitleLabelValuePair, HeaderLabelValuePair } from '@strata/react-label-value-pairs';
import { SuccessToast } from '@strata/react-toast-message';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { submitReferral } from 'api/referralAPI';
import { ClinicalInformation } from 'components/referralForm/ClinicalInformation';
import { CancelReferralDialog } from 'components/referralForm/dialogs/CancelReferralDialog';
import { MedicationAndDiagnosis } from 'components/referralForm/MedicationAndDiagnosis';
import { PlaceOfServiceAndDelivery } from 'components/referralForm/PlaceOfServiceAndDelivery';
import { PrescriberAndPatient } from 'components/referralForm/PrescriberAndPatient';
import { PrescribingInformation } from 'components/referralForm/PrescribingInformation';
import { ReferralSubmission } from 'components/referralForm/ReferralSubmission';
import { handleError } from 'services/Error';
import { AppRoutes } from 'services/routes';
import { FormSections } from 'store/referralForm/referralFormSchema';
import { closeSection, openSection, resetForm } from 'store/referralForm/referralFormSlice';

const FormSectionComponentMap = {
    [FormSections.PRESCRIBER_AND_PATIENT]: {
        sectionTitle: 'Prescriber & Patient',
        SectionComponent: PrescriberAndPatient,
    },
    [FormSections.MEDICATION_AND_DIAGNOSIS]: {
        sectionTitle: 'Medication & Diagnosis',
        SectionComponent: MedicationAndDiagnosis,
    },
    [FormSections.CLINICAL_INFORMATION]: {
        sectionTitle: 'Clinical Information',
        SectionComponent: ClinicalInformation,
    },
    [FormSections.PRESCRIBING_INFORMATION]: {
        sectionTitle: 'Prescribing Information',
        SectionComponent: PrescribingInformation,
    },
    [FormSections.PLACE_OF_SERVICE_AND_DELIVERY]: {
        sectionTitle: 'Place Of Service & Delivery',
        SectionComponent: PlaceOfServiceAndDelivery,
    },
    [FormSections.REFERRAL_SUBMISSION]: {
        sectionTitle: 'Referral Submission',
        SectionComponent: ReferralSubmission,
    },
};

export const ReferralFormPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useAuth0();
    const { sections, capturedData, activeSection } = useSelector((state) => state.referralForm);
    const [showCancelReferralDialog, setShowCancelReferralDialog] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isFormComplete = () =>
        Object.values(sections).every(
            (section) =>
                section.progressIndicatorStatus === FormAccordion.statuses.COMPLETED ||
                section.progressIndicatorStatus === FormAccordion.statuses.WARNING,
        );

    const onFormSubmit = async () => {
        // Ensure terms and conditions have been accepted
        if (isFormComplete()) {
            try {
                setIsSubmitting(true);

                await submitReferral(capturedData, user);

                // Submit referral was successful
                dispatch(resetForm());
                SuccessToast({ message: 'Your referral was submitted successfully.' });
            } catch (err) {
                handleError('Unable to submit referral. Please try again.', err);
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const intakeFormSections = Object.values(FormSections).map((section) => {
        const { sectionTitle, SectionComponent } = FormSectionComponentMap[section];
        const { isOpen, progressIndicatorStatus } = sections[section];
        return {
            sectionTitle,
            content: <SectionComponent section={section} isOpen={isOpen} width="40%" />,
            step: section,
            progressIndicatorStatus,
            isOpen,
            onSectionClick: () => dispatch(!isOpen ? openSection(section) : closeSection(section)),
            disabled: progressIndicatorStatus === FormAccordion.statuses.NOT_STARTED,
            displayFooter: section === Object.values(FormSections).length,
            scrollToTopOnOpen: true,
            scrollOffset: 44,
        };
    });

    return (
        <>
            <FullscreenLoading isShowing={isSubmitting} loadingText="Submitting referral..." />
            <CancelReferralDialog
                isOpen={showCancelReferralDialog}
                onClose={() => setShowCancelReferralDialog(false)}
            />
            <FormHeaderAndContent
                rowOneContentHeader={
                    <ActionButtonText
                        label="Back to Home"
                        isBackNavigationButton
                        onClick={() => navigate(AppRoutes.home)}
                    />
                }
                rowTwoContentHeader={
                    activeSection !== 1 && (
                        <>
                            <FormTitleLabelValuePair
                                label={formatNameLastFirst(
                                    capturedData.memberLastName,
                                    capturedData.memberFirstName,
                                )}
                                value={formatAddress(
                                    capturedData.memberCity,
                                    capturedData.memberState,
                                    capturedData.memberZip,
                                )}
                            />
                            <HeaderLabelValuePair label="Age" value={capturedData.memberAge} />
                            <HeaderLabelValuePair
                                label="Gender"
                                value={formatGender(capturedData.memberGender)}
                            />
                            <HeaderLabelValuePair
                                label="Prescriber"
                                value={capturedData.prescriberName}
                            />
                            <HeaderLabelValuePair
                                label="Health Plan"
                                value={capturedData.segmentName}
                            />
                        </>
                    )
                }
                mainContent={
                    <FormAccordion
                        sectionDataArray={intakeFormSections}
                        secondaryButtonText="Cancel Referral"
                        primaryButtonText="Submit Referral"
                        onCancelClick={() => setShowCancelReferralDialog(true)}
                        onSubmitClick={onFormSubmit}
                        // Only enable submit button if terms & conditions have been agreed to
                        isSubmitting={!isFormComplete() || isSubmitting}
                    />
                }
            />
        </>
    );
};
